import React, { useEffect, useMemo, useState } from 'react';
import { LayoutGrid, Clock } from 'lucide-react';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';
import ErrorState from '../shared/ErrorState';
import { supabase } from '../../supabaseClient';

// --- formatação (padrão pt-BR do projeto; null/0 → "—") ---------------------
function fmtBRL(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n) || n === 0) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

function fmtArea(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return `${n.toLocaleString('pt-BR')} m²`;
}

function fmtDate(d) {
  if (!d) return null;
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return null; }
}

// --- status badge -----------------------------------------------------------
const STATUS_META = {
  ofertada_nao_confirmada: { label: 'Ofertada (não confirmada)', color: 'bg-amber-50 text-amber-700' },
  disponivel:              { label: 'Disponível',                color: 'bg-emerald-50 text-emerald-700' },
  reservado:               { label: 'Reservado',                 color: 'bg-blue-50 text-blue-700' },
  vendido:                 { label: 'Vendido',                   color: 'bg-slate-100 text-slate-600' },
  indisponivel:            { label: 'Indisponível',              color: 'bg-slate-100 text-slate-600' },
};

function statusMeta(status) {
  if (!status) return { label: '—', color: 'bg-slate-50 text-slate-500' };
  return STATUS_META[status] || { label: status, color: 'bg-slate-50 text-slate-600' };
}

// --- unit_kind label --------------------------------------------------------
const UNIT_KIND_LABEL = {
  residential:     'Residencial',
  office:          'Sala comercial',
  parking:         'Vaga',
  package:         'Pacote',
  corporate_floor: 'Laje corporativa',
  model:           'Modelo',
  retail:          'Loja',
};

function unitKindLabel(kind) {
  if (!kind) return null;
  return UNIT_KIND_LABEL[kind] || (kind.charAt(0).toUpperCase() + kind.slice(1));
}

export default function UnidadesSection({ empreendimentoId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!empreendimentoId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: err } = await supabase
        .from('v_empreendimento_unidades')
        .select('*')
        .eq('empreendimento_id', empreendimentoId)
        .order('torre', { nullsFirst: true })
        .order('identificador');
      if (cancelled) return;
      if (err) { setError(err.message); setLoading(false); return; }
      setRows(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [empreendimentoId]);

  // resumo do header: faixa de "a partir de" + freshness do preço mais recente
  const summary = useMemo(() => {
    const froms = rows.map((r) => r.price_from).filter((v) => v != null && Number(v) > 0).map(Number);
    const min = froms.length ? Math.min(...froms) : null;
    const max = froms.length ? Math.max(...froms) : null;
    let fresh = null;
    for (const r of rows) {
      if (!r.price_valid_from) continue;
      if (!fresh || new Date(r.price_valid_from) > new Date(fresh.price_valid_from)) {
        fresh = { price_valid_from: r.price_valid_from, price_source_reference: r.price_source_reference };
      }
    }
    return { min, max, fresh };
  }, [rows]);

  return (
    <section className="bg-white border border-slate-200 rounded p-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <LayoutGrid size={14} className="text-slate-400" />
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Unidades</h3>
        <span className="text-[11px] text-slate-400">({rows.length})</span>
        {(summary.min != null || summary.max != null) && (
          <span className="text-[11px] text-slate-500">
            · a partir de {summary.min === summary.max ? fmtBRL(summary.min) : `${fmtBRL(summary.min)} – ${fmtBRL(summary.max)}`}
          </span>
        )}
        {summary.fresh && fmtDate(summary.fresh.price_valid_from) && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-400">
            <Clock size={10} />
            preço de {fmtDate(summary.fresh.price_valid_from)}
            {summary.fresh.price_source_reference ? ` · ${summary.fresh.price_source_reference}` : ''}
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-4"><LoadingSpinner /></div>
      ) : error ? (
        <ErrorState message={error} />
      ) : rows.length === 0 ? (
        <EmptyState message="Nenhuma unidade cadastrada para este empreendimento." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left">Identificador</th>
                <th className="px-2 py-1.5 text-left">Tipologia</th>
                <th className="px-2 py-1.5 text-right">Área priv. (m²)</th>
                <th className="px-2 py-1.5 text-right">Vagas</th>
                <th className="px-2 py-1.5 text-right">Condomínio (R$)</th>
                <th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-right">Tabela (R$)</th>
                <th className="px-2 py-1.5 text-right">A partir de (R$)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((u) => {
                const sm = statusMeta(u.status);
                const kind = unitKindLabel(u.unit_kind);
                const subParts = [
                  u.torre || null,                                              // já vem "Torre Nature"
                  u.andar || null,                                              // já vem "19º" / "Andar Médio"
                  (u.unit_kind && u.unit_kind !== 'residential') ? kind : null, // kind só se não residencial
                ].filter(Boolean);
                return (
                  <tr key={u.unidade_id}>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-slate-700 font-medium">{u.identificador || u.original_label || '—'}</span>
                        {u.unit_count != null && Number(u.unit_count) > 1 && (
                          <span className="text-[10px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded">×{u.unit_count}</span>
                        )}
                      </div>
                      {subParts.length > 0 && (
                        <div className="text-[10px] text-slate-400">{subParts.join(' · ')}</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">{u.tipologia_label || '—'}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{fmtArea(u.area_privativa)}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{u.vagas != null ? Number(u.vagas).toLocaleString('pt-BR') : '—'}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{fmtBRL(u.condominio_value)}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${sm.color}`}>{sm.label}</span>
                        {u.needs_review && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-600">revisar</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{fmtBRL(u.price_tabela)}</td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{fmtBRL(u.price_from)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
